import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LangContext';

const content = {
  en: {
    // SEO Metadata
    title: 'Medical Aesthetics FAQ & Treatment Guide | Halo Richmond Hill',
    description: 'Find answers to common questions about injectable fillers, laser skin treatments, consultation process, and post-care at Halo Aesthetic Clinic Richmond Hill.',
    
    // Page Content
    pageTitle: 'Frequently Asked Questions',
    pageSubtitle: 'Find answers to common questions about our aesthetic treatments and services.',
    
    // FAQ Questions and Answers
    faqs: [
      {
        question: 'Where can I get aesthetic services after work hours on Friday?',
        answer: 'Halo Aesthetic Clinic offers extended hours every Friday from 1PM to 9PM, making it convenient for busy professionals. All our premium services are available during these after-hours appointments.',
        linkText: 'Learn more about our after-hours services',
        linkPath: '/after-hours-services'
      },
      {
        question: 'What treatments are best for Asian skin types?',
        answer: 'Asian skin requires specialized care due to unique characteristics like higher melanin content and sensitivity. We offer customized treatments for hyperpigmentation, melasma, and gentle skincare regimens.',
        linkText: 'Discover our specialized Asian skin treatment options',
        linkPath: '/asian-skin-treatment'
      },
      {
        question: 'Where can I get Ulthera Prime treatment in Richmond Hill?',
        answer: 'Halo Aesthetic Clinic in Richmond Hill offers advanced Ulthera Prime non-invasive skin tightening. This FDA-approved treatment provides natural-looking results with no downtime.',
        linkText: 'Find out more about Ulthera Prime in Richmond Hill',
        linkPath: '/ulthera-richmond-hill'
      },
      {
        question: 'How long do aesthetic treatment results last?',
        answer: 'Treatment duration varies by procedure. Injectable treatments typically last 3-6 months, while Ulthera Prime results can improve for up to 6 months and last over a year. Consult with our specialists for personalized expectations.',
        linkText: null,
        linkPath: null
      },
      {
        question: 'Is there any downtime after aesthetic treatments?',
        answer: 'Most treatments have minimal to no downtime. Injectable treatments may have slight redness that fades quickly. Ulthera Prime requires no downtime, allowing immediate return to daily activities. Some procedures may require brief recovery periods.',
        linkText: null,
        linkPath: null
      },
      {
        question: 'What should I expect during my first consultation?',
        answer: 'Your first consultation includes a thorough skin assessment, discussion of your aesthetic goals, and a personalized treatment plan. Our specialists will explain procedures, expected results, and answer all your questions in a comfortable environment.',
        linkText: null,
        linkPath: null
      },
      {
        question: 'Are aesthetic treatments safe for all skin types?',
        answer: 'Safety depends on individual skin characteristics and medical history. We specialize in Asian skin treatments and customize all procedures to your specific needs. A comprehensive consultation ensures safe and effective treatment planning.',
        linkText: 'Learn about our specialized Asian skin treatment',
        linkPath: '/asian-skin-treatment'
      },
      {
        question: 'Can I combine multiple aesthetic treatments?',
        answer: 'Yes, many treatments can be safely combined for enhanced results. Our practitioners will create a customized treatment plan that may include injectables, skincare procedures, and body contouring. Schedule a consultation to discuss your options.',
        linkText: null,
        linkPath: null
      },
      {
        question: 'How do I prepare for an aesthetic treatment appointment?',
        answer: 'Preparation varies by treatment type. Generally, avoid sun exposure, certain medications, and alcohol before procedures. We provide detailed pre-treatment instructions during consultation.',
        linkText: 'Book your after-hours appointment',
        linkPath: '/after-hours-services'
      },
      {
        question: 'What is the difference between Ulthera Prime and traditional facelifts?',
        answer: 'Ulthera Prime is non-invasive, requires no surgery or downtime, and uses focused ultrasound to stimulate natural collagen production. Traditional facelifts are surgical procedures with longer recovery. Ulthera Prime provides gradual, natural-looking results.',
        linkText: 'Learn more about Ulthera Prime treatment',
        linkPath: '/ulthera-richmond-hill'
      },
      {
        question: 'Do you offer payment plans for aesthetic treatments?',
        answer: 'We offer flexible payment options to make treatments accessible. During your consultation, we can discuss payment plans and financing options that work with your budget. Contact us to learn more about available payment methods.',
        linkText: null,
        linkPath: null
      },
      {
        question: 'How often should I schedule maintenance treatments?',
        answer: 'Maintenance frequency depends on treatment type and individual factors. Injectable treatments typically require touch-ups every 3-6 months, while Ulthera Prime may need annual sessions. Our specialists will recommend a personalized maintenance schedule.',
        linkText: null,
        linkPath: null
      }
    ],
    
    // JSON-LD Placeholder Note
    structuredDataNote: 'JSON-LD structured data (FAQPage schema) will be implemented in Stage 3'
  },
  cn: {
    // SEO Metadata
    title: '医美常见问题解答与疗程指南 | Halo Richmond Hill 医美诊所',
    description: '解答关于注射美容、玻尿酸、光电皮肤管理、面诊流程及术后护理的常见问题。',
    
    // Page Content
    pageTitle: '常见问题',
    pageSubtitle: '查找关于我们医美治疗和服务的常见问题答案。',
    
    // FAQ Questions and Answers
    faqs: [
      {
        question: '周五下班后哪里可以做医美服务？',
        answer: 'Halo医美中心每周五提供延长服务时间，从下午1点到晚上9点，方便忙碌的专业人士。我们所有的优质服务在这些延长服务时间内都可提供。',
        linkText: '了解更多关于我们的延长服务时间',
        linkPath: '/after-hours-services'
      },
      {
        question: '什么治疗最适合亚洲肤质？',
        answer: '亚洲皮肤由于独特的特性（如较高的黑色素含量和敏感性）需要专业护理。我们提供针对色素沉着、黄褐斑和温和护肤方案的定制治疗。',
        linkText: '了解我们专业的亚洲皮肤治疗方案',
        linkPath: '/asian-skin-treatment'
      },
      {
        question: 'Richmond Hill哪里可以做Ulthera Prime治疗？',
        answer: 'Halo医美中心在Richmond Hill提供先进的Ulthera Prime非侵入性皮肤紧致治疗。这种FDA批准的治疗提供自然效果，无需恢复期。',
        linkText: '了解更多关于Richmond Hill的Ulthera Prime',
        linkPath: '/ulthera-richmond-hill'
      },
      {
        question: '医美治疗效果能持续多久？',
        answer: '治疗持续时间因程序而异。注射治疗通常持续3-6个月，而Ulthera Prime效果可以改善长达6个月，持续一年以上。与我们的专家咨询以获得个性化预期。',
        linkText: null,
        linkPath: null
      },
      {
        question: '医美治疗后需要恢复期吗？',
        answer: '大多数治疗几乎没有或没有恢复期。注射治疗可能有轻微红肿，会很快消退。Ulthera Prime无需恢复期，可立即恢复日常活动。某些程序可能需要短暂的恢复期。',
        linkText: null,
        linkPath: null
      },
      {
        question: '第一次咨询时我应该期待什么？',
        answer: '您的第一次咨询包括全面的皮肤评估、讨论您的医美目标以及个性化治疗计划。我们的专家将解释程序、预期效果，并在舒适的环境中回答您的所有问题。',
        linkText: null,
        linkPath: null
      },
      {
        question: '医美治疗对所有肤质都安全吗？',
        answer: '安全性取决于个人皮肤特征和医疗史。我们专门从事亚洲皮肤治疗，并根据您的特定需求定制所有程序。全面咨询确保安全有效的治疗计划。',
        linkText: '了解我们专业的亚洲皮肤治疗',
        linkPath: '/asian-skin-treatment'
      },
      {
        question: '我可以结合多种医美治疗吗？',
        answer: '是的，许多治疗可以安全结合以获得更好的效果。我们的医师将创建可能包括注射、皮肤护理程序和身体塑形的定制治疗计划。预约咨询以讨论您的选择。',
        linkText: null,
        linkPath: null
      },
      {
        question: '如何准备医美治疗预约？',
        answer: '准备因治疗类型而异。通常，在程序前避免阳光照射、某些药物和酒精。我们在咨询期间提供详细的治疗前说明。',
        linkText: '预约您的延长服务时间',
        linkPath: '/after-hours-services'
      },
      {
        question: 'Ulthera Prime和传统拉皮有什么区别？',
        answer: 'Ulthera Prime是非侵入性的，无需手术或恢复期，使用聚焦超声波刺激天然胶原蛋白生成。传统拉皮是手术程序，恢复期更长。Ulthera Prime提供逐渐的自然效果。',
        linkText: '了解更多关于Ulthera Prime治疗',
        linkPath: '/ulthera-richmond-hill'
      },
      {
        question: '你们提供医美治疗的分期付款吗？',
        answer: '我们提供灵活的付款选项，使治疗更容易获得。在咨询期间，我们可以讨论适合您预算的分期付款和融资选项。联系我们了解更多可用的付款方式。',
        linkText: null,
        linkPath: null
      },
      {
        question: '我应该多久安排一次维护治疗？',
        answer: '维护频率取决于治疗类型和个人因素。注射治疗通常需要每3-6个月进行一次补针，而Ulthera Prime可能需要年度疗程。我们的专家将推荐个性化的维护计划。',
        linkText: null,
        linkPath: null
      }
    ],
    
    // JSON-LD Placeholder Note
    structuredDataNote: 'JSON-LD结构化数据（FAQPage schema）将在Stage 3中实现'
  }
};

const FAQ = () => {
  const { lang } = useLang();
  const c = content[lang];

  // JSON-LD Structured Data - FAQPage Schema
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.haloaestheticclinic.com/faq#webpage",
    "mainEntity": c.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer + (faq.linkText ? ` ${faq.linkText}` : '')
      }
    }))
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": "https://www.haloaestheticclinic.com/#organization",
    "name": "Halo Aesthetic Clinic",
    "url": "https://www.haloaestheticclinic.com",
    "image": "https://www.haloaestheticclinic.com/logo.jpg",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.haloaestheticclinic.com/logo.jpg"
    },
    "telephone": "+14377555409",
    "email": "info@haloaestheticclinic.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1595 16th Ave Suite 103",
      "addressLocality": "Richmond Hill",
      "addressRegion": "ON",
      "postalCode": "L4B 3N9",
      "addressCountry": "CA"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Tuesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Wednesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Thursday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Friday",
        "opens": "13:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "19:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "medicalSpecialty": "Dermatology"
  };

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
        <meta name="description" content={c.description} />
        <link rel="canonical" href="https://www.haloaestheticclinic.com/faq" />
        <meta name="keywords" content={lang === 'en'
          ? 'aesthetic treatment FAQ, medical aesthetics questions, beauty treatment answers, aesthetic clinic FAQ'
          : '医美治疗常见问题, 医美问题, 美容治疗答案, 医美诊所常见问题'
        } />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      
      <div className="bg-softGradient bg-no-repeat bg-top text-text">
        {/* Page Header */}
        <section className="py-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {c.pageTitle}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {c.pageSubtitle}
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {c.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                >
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                    <span className="flex items-center justify-between">
                      <span>{faq.question}</span>
                      <svg
                        className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{
                          transform: 'rotate(0deg)'
                        }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                    <p className="mb-2">
                      {faq.answer}
                      {faq.linkText && faq.linkPath && (
                        <>
                          {' '}
                          <Link
                            to={faq.linkPath}
                            className="text-[#2E6B4C] font-semibold hover:underline transition-colors"
                          >
                            {faq.linkText}
                          </Link>
                          .
                        </>
                      )}
                    </p>
                  </div>
                </details>
              ))}
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="py-section bg-softRadial">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              {lang === 'en' ? 'Still Have Questions?' : '还有问题？'}
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              {lang === 'en'
                ? 'Contact our team for personalized consultation and expert advice on your aesthetic treatment options.'
                : '联系我们的团队，获得关于您医美治疗选择的个性化咨询和专业建议。'
              }
            </p>
            <Link
              to="/contact"
              className="inline-block px-6 py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
              }}
            >
              {lang === 'en' ? 'Contact Us' : '联系我们'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;

